import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Browser IANA timezone (e.g. "America/New_York"), sent to analytics endpoints
// so day buckets align with the end user's local midnight (EIP-241).
// Pre-encoded for direct use in query strings (zones like "Etc/GMT+8" need it).
export const BROWSER_TZ = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('eva_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and network issues
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors (backend not reachable)
    if (!error.response) {
      // Network error - create a user-friendly error
      const networkError = new Error(
        error.code === 'ECONNREFUSED' || error.message === 'Network Error'
          ? 'Unable to connect to server. Please check if the backend is running.'
          : `Connection failed: ${error.message}`
      );
      networkError.isNetworkError = true;
      networkError.response = {
        status: 0,
        data: {
          detail: networkError.message
        }
      };
      return Promise.reject(networkError);
    }
    
    // Only redirect on 401 if NOT on auth pages (login, signup, forgot-password, reset-password)
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      // '/sso' is a public route too — a 401 there was wiping the session and
      // bouncing to /login mid-handoff instead of letting the page show the error.
      const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password', '/accept-invitation', '/accept-superadmin-invitation', '/sso'];
      const isAuthPage = authPaths.some(path => currentPath.startsWith(path));
      
      if (!isAuthPage) {
        // Clear the full session (not just token+user) so no stale tenant /
        // superadmin context survives, then surface why on the login page.
        localStorage.removeItem('eva_token');
        localStorage.removeItem('eva_user');
        localStorage.removeItem('eva_tenant');
        localStorage.removeItem('eva_viewing_as_superadmin');
        window.location.href = '/login?expired=1';
      }
    }

    // Subscription ended (EIP-322). The server's 403 is the authoritative signal —
    // more current than the cached `eva_tenant` the banner otherwise reads, which is
    // hydrated at login and refreshed from only two call sites. Broadcasting it lets
    // the UI catch up the moment the boundary is actually crossed, rather than at the
    // user's next login. Deliberately no redirect: read-only degrade means the page
    // they are on keeps working.
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'subscription_expired') {
        window.dispatchEvent(new CustomEvent('eva:subscription-expired', { detail }));
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    try {
      // Always specify eva_insights product since this is the Eva Insights frontend
      const response = await api.post('/auth/login', { email, password, product: 'eva_insights' });
      return response.data;
    } catch (error) {
      // Re-throw with better error message for common issues
      if (error.isNetworkError) {
        throw error;
      }
      if (error.message?.includes('JSON')) {
        const jsonError = new Error('Server returned an invalid response. The backend may be starting up or experiencing issues.');
        jsonError.response = { data: { detail: jsonError.message } };
        throw jsonError;
      }
      throw error;
    }
  },
  // Second half of a superadmin login (EIP-319). Keyed on the opaque step-up
  // token issued by /auth/login, never on an email — the server reads the
  // identity from the token's row, so these carry no user-supplied identity.
  stepUpOptions: async (stepUpToken) => {
    const response = await api.post('/passkey/step-up/options', {
      step_up_token: stepUpToken,
    });
    return response.data;
  },
  // challenge_id is echoed back from stepUpOptions. Required (unlike the
  // registration equivalent) so the server matches this exact ceremony rather
  // than "newest challenge for this email".
  stepUpVerify: async (stepUpToken, credential, challengeId) => {
    const response = await api.post('/passkey/step-up/verify', {
      step_up_token: stepUpToken,
      credential,
      challenge_id: challengeId,
    });
    return response.data;
  },
  mockLogin: async (email) => {
    const response = await api.post('/auth/mock-login', { email });
    return response.data;
  },
  signup: async (data) => {
    const response = await api.post('/auth/signup', data);
    return response.data;
  },
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  },
  getPasswordRequirements: async () => {
    const response = await api.get('/auth/password-requirements');
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('eva_token');
    localStorage.removeItem('eva_user');
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  createEvaPeopleSSOLink: async () => {
    const response = await api.post('/auth/eva-people/sso-link');
    return response.data;
  },
};

// eva-website admin panel cross-origin handoff (EIP-330). Mints a one-shot
// code once a superadmin has explicitly confirmed the handoff from
// AdminSsoAuthorizePage — see that page for the full flow.
export const adminSsoAPI = {
  issueCode: async (returnUrl, state) => {
    const response = await api.post('/auth/admin-sso/issue-code', {
      return_url: returnUrl,
      state,
    });
    return response.data;
  },
};

// Passkey (WebAuthn) APIs — superadmin second factor, EIP-305.
// Previously these paths were called inline off the raw `api` instance from two
// different components.
export const passkeyAPI = {
  getStatus: async () => {
    const response = await api.get('/passkey/status');
    return response.data;
  },
  listCredentials: async () => {
    const response = await api.get('/passkey/credentials');
    return response.data;
  },
  registerOptions: async (deviceName) => {
    const response = await api.post('/passkey/register/options', {
      device_name: deviceName || undefined,
    });
    return response.data;
  },
  // challenge_id is echoed back from registerOptions so the server matches the
  // ceremony by id rather than "newest challenge for this email".
  registerVerify: async (credential, deviceName, challengeId) => {
    const response = await api.post('/passkey/register/verify', {
      credential,
      device_name: deviceName || 'My Passkey',
      challenge_id: challengeId,
    });
    return response.data;
  },
  deleteCredential: async (credentialId) => {
    const response = await api.delete(`/passkey/credentials/${credentialId}`);
    return response.data;
  },
};

// Superadmin APIs
export const superadminAPI = {
  checkStatus: async () => {
    const response = await api.get('/superadmin/check');
    return response.data;
  },
  listTenants: async () => {
    const response = await api.get('/superadmin/tenants');
    return response.data;
  },
  listPendingSignups: async () => {
    const response = await api.get('/superadmin/pending-signups');
    return response.data;
  },
  listSignupActivity: async (filters = {}) => {
    const response = await api.get('/superadmin/signup-activity', { params: filters });
    return response.data;
  },
  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },
  createTenant: async (tenantData) => {
    const response = await api.post('/superadmin/tenants', tenantData);
    return response.data;
  },
  getTenant: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}`);
    return response.data;
  },
  updateTenant: async (tenantId, updates) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}`, updates);
    return response.data;
  },
  switchTenant: async (tenantId) => {
    const response = await api.post('/superadmin/switch-tenant', { tenant_id: tenantId });
    return response.data;
  },
  returnHome: async () => {
    const response = await api.post('/superadmin/return-home');
    return response.data;
  },
  listSuperadmins: async () => {
    const response = await api.get('/superadmin/superadmins');
    return response.data;
  },
  addSuperadmin: async (email, name) => {
    const response = await api.post('/superadmin/superadmins', { email, name });
    return response.data;
  },
  deactivateSuperadmin: async (email) => {
    const response = await api.post(`/superadmin/superadmins/${encodeURIComponent(email)}/deactivate`);
    return response.data;
  },
  reactivateSuperadmin: async (email) => {
    const response = await api.post(`/superadmin/superadmins/${encodeURIComponent(email)}/reactivate`);
    return response.data;
  },
  hardDeleteSuperadmin: async (email) => {
    const response = await api.delete(`/superadmin/superadmins/${encodeURIComponent(email)}/hard`);
    return response.data;
  },
  // Superadmin Invitations
  sendSuperadminInvitation: async (email, name) => {
    const response = await api.post('/superadmin/invitations', { email, name });
    return response.data;
  },
  listSuperadminInvitations: async () => {
    const response = await api.get('/superadmin/invitations');
    return response.data;
  },
  cancelSuperadminInvitation: async (invitationId) => {
    const response = await api.delete(`/superadmin/invitations/${invitationId}`);
    return response.data;
  },
  resendSuperadminInvitation: async (invitationId) => {
    const response = await api.post(`/superadmin/invitations/resend/${invitationId}`);
    return response.data;
  },
  verifySuperadminInvitation: async (token) => {
    const response = await api.post('/superadmin/invitations/verify', { token });
    return response.data;
  },
  acceptSuperadminInvitation: async (token, password) => {
    const response = await api.post('/superadmin/invitations/accept', { token, password });
    return response.data;
  },
  deactivateTenant: async (tenantId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/deactivate`);
    return response.data;
  },
  activateTenant: async (tenantId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/activate`);
    return response.data;
  },
  deleteTenant: async (tenantId) => {
    const response = await api.delete(`/superadmin/tenants/${tenantId}`);
    return response.data;
  },
  // Analytics & Trial Management
  getTenantsSummary: async () => {
    const response = await api.get('/superadmin/tenants-summary');
    return response.data;
  },
  getTenantAnalytics: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/analytics`);
    return response.data;
  },
  updateTenantTrial: async (tenantId, config) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/trial`, config);
    return response.data;
  },
  getTenantLimits: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/limits`);
    return response.data;
  },
  getTenantBilling: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/billing`);
    return response.data;
  },
  getTenantProfile: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/profile`);
    return response.data;
  },
  updateTenantProfile: async (tenantId, profile) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/profile`, profile);
    return response.data;
  },
  updateTenantLimits: async (tenantId, limits) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/limits`, limits);
    return response.data;
  },
  updateTenantSubscription: async (tenantId, update) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/subscription`, update);
    return response.data;
  },
  updateTenantUnlimited: async (tenantId, enabled) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/unlimited`, { enabled });
    return response.data;
  },
  startTenantTrial: async (tenantId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/start-trial`);
    return response.data;
  },
  getTenantTheme: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/theme`);
    return response.data;
  },
  updateTenantTheme: async (tenantId, theme) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/theme`, theme);
    return response.data;
  },
  getPricingTiers: async () => {
    const response = await api.get('/superadmin/pricing-tiers');
    return response.data;
  },
  // Default Trial Config
  getDefaultTrialConfig: async () => {
    const response = await api.get('/superadmin/default-trial-config');
    return response.data;
  },
  updateDefaultTrialConfig: async (config) => {
    const response = await api.put('/superadmin/default-trial-config', config);
    return response.data;
  },
  convertTenantToPaid: async (tenantId, options) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/convert-to-paid`, options);
    return response.data;
  },
  recordTenantPayment: async (tenantId, payload) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/record-payment`, payload);
    return response.data;
  },
  terminateSubscription: async (tenantId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/terminate`);
    return response.data;
  },
  // Integrations Management
  getIntegrations: async () => {
    const response = await api.get('/integrations');
    return response.data;
  },
  getIntegration: async (type) => {
    const response = await api.get(`/integrations/${type}`);
    return response.data;
  },
  updateIntegration: async (type, data) => {
    const response = await api.put(`/integrations/${type}`, data);
    return response.data;
  },
  testIntegration: async (type) => {
    const response = await api.post(`/integrations/${type}/test`);
    return response.data;
  },
  triggerSync: async (type) => {
    const response = await api.post(`/integrations/${type}/sync`);
    return response.data;
  },
  getIntegrationLogs: async (type, limit = 50) => {
    const response = await api.get(`/integrations/${type}/logs?limit=${limit}`);
    return response.data;
  },
  // Send test email
  sendTestEmail: async (toEmail) => {
    const response = await api.post('/integrations/email/test-send', { to_email: toEmail });
    return response.data;
  },
  // Get integration status overview (stoplight dashboard)
  getIntegrationStatusOverview: async () => {
    const response = await api.get('/integrations/status/overview');
    return response.data;
  },
  // Session management
  getTenantSessions: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/sessions`);
    return response.data;
  },
  revokeSession: async (tenantId, sessionId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/sessions/${sessionId}/revoke`);
    return response.data;
  },
  revokeAllSessions: async (tenantId) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/sessions/revoke-all`);
    return response.data;
  },
  getSessionsSummary: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/sessions/summary`);
    return response.data;
  },
  getTenantChatSessions: async (tenantId, limit = 50, offset = 0) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/chat-sessions?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  // Token Usage Management
  getTenantTokenUsage: async (tenantId) => {
    const response = await api.get(`/usage/superadmin/${tenantId}`);
    return response.data;
  },
  updateTenantPlan: async (tenantId, planType) => {
    const response = await api.put(`/usage/superadmin/${tenantId}/plan`, { plan_type: planType });
    return response.data;
  },
  resetTenantUsage: async (tenantId) => {
    const response = await api.post(`/usage/superadmin/${tenantId}/reset`);
    return response.data;
  },
  getAvailablePlans: async () => {
    const response = await api.get('/usage/plans');
    return response.data;
  },
  // Access Requests Management
  listAllAccessRequests: async () => {
    const response = await api.get('/auth/access-requests/all');
    return response.data;
  },
  reviewAccessRequest: async (requestId, payload) => {
    const response = await api.put(`/auth/access-requests/${requestId}`, payload);
    return response.data;
  },
  // Tenant Config (AI models, session, topics, file types)
  getTenantConfig: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/config`);
    return response.data;
  },
  updateTenantConfig: async (tenantId, config) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/config`, config);
    return response.data;
  },
  // Default settings for new tenants
  getDefaultSettings: async () => {
    const response = await api.get('/superadmin/defaults');
    return response.data;
  },
  updateDefaultSettings: async (settings) => {
    const response = await api.put('/superadmin/defaults', settings);
    return response.data;
  },
};

// Connector Vote APIs (global)
export const connectorVotesAPI = {
  getVotes: async () => {
    const response = await api.get('/connectors/votes/all');
    return response.data;
  },
  toggleVote: async (connectorName) => {
    const response = await api.post(`/connectors/votes/${encodeURIComponent(connectorName)}`);
    return response.data;
  },
};

// Support Ticket APIs
export const supportAPI = {
  getTickets: async () => {
    const response = await api.get('/settings/support/tickets');
    return response.data;
  },
  createTicket: async (data) => {
    const response = await api.post('/settings/support/tickets', data);
    return response.data;
  },
};

// Employee bulk import APIs (EIP-296)
export const employeesAPI = {
  // Dry-run: classify every row and check seat capacity WITHOUT creating users,
  // so the wizard's preview can show duplicates / seat limits before commit.
  validateBulkImport: async (employees) => {
    const response = await api.post('/employees/bulk-import/validate', { employees });
    return response.data;
  },
  // Commit the import (the server validates + writes the batch atomically).
  bulkImport: async (employees) => {
    const response = await api.post('/employees/bulk-import', { employees });
    return response.data;
  },
};

// Analytics APIs
export const analyticsAPI = {
  getUsage: async (window = '30d') => {
    const response = await api.get(`/analytics/usage?window=${window}&tz=${BROWSER_TZ}`);
    return response.data;
  },
  getUsageUsers: async (window = '30d') => {
    const response = await api.get(`/analytics/usage/users?window=${window}`);
    return response.data;
  },
  getFeedback: async (window = '14d') => {
    const response = await api.get(`/analytics/feedback?window=${window}&tz=${BROWSER_TZ}`);
    return response.data;
  },
  getConversations: async (window = '14d') => {
    const response = await api.get(`/analytics/conversations?window=${window}&tz=${BROWSER_TZ}`);
    return response.data;
  },
  getKnowledgeBase: async (window) => {
    const query = window ? `?window=${encodeURIComponent(window)}` : '';
    const response = await api.get(`/analytics/knowledge-base${query}`);
    return response.data;
  },
};

// Analytics V2 APIs (DuckDB OLAP)
export const analyticsV2API = {
  // Document Performance
  getDocumentPerformance: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.topic) queryParams.append('topic', params.topic);
    if (params.status) queryParams.append('status', params.status);
    if (params.minHealthScore) queryParams.append('min_health_score', params.minHealthScore);
    if (params.trend) queryParams.append('trend', params.trend);
    if (params.sortBy) queryParams.append('sort_by', params.sortBy);
    if (params.sortOrder) queryParams.append('sort_order', params.sortOrder);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const response = await api.get(`/analytics/v2/documents/performance?${queryParams.toString()}`);
    return response.data;
  },
  getSingleDocumentPerformance: async (documentId) => {
    const response = await api.get(`/analytics/v2/documents/${documentId}/performance`);
    return response.data;
  },
  
  // Topic Analytics
  getTopicAnalytics: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.minConversations) queryParams.append('min_conversations', params.minConversations);
    if (params.trend) queryParams.append('trend', params.trend);
    
    const response = await api.get(`/analytics/v2/topics/analytics?${queryParams.toString()}`);
    return response.data;
  },
  
  // Content Gaps (DuckDB version)
  getContentGapsEnriched: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.minPriority) queryParams.append('min_priority', params.minPriority);
    if (params.topic) queryParams.append('topic', params.topic);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const response = await api.get(`/analytics/v2/content-gaps?${queryParams.toString()}`);
    return response.data;
  },
  
  // Regional Analytics
  getRegionalHeatmap: async () => {
    const response = await api.get('/analytics/v2/regional/heatmap');
    return response.data;
  },
  
  // Superadmin Analytics
  getTenantUsage: async () => {
    const response = await api.get('/analytics/v2/superadmin/tenant-usage');
    return response.data;
  },
  getAuditSummary: async (days = 7) => {
    const response = await api.get(`/analytics/v2/superadmin/audit-summary?days=${days}`);
    return response.data;
  },
  
  // Status & Management
  getStatus: async () => {
    const response = await api.get('/analytics/v2/status');
    return response.data;
  },
  refreshModels: async (fullRefresh = false) => {
    const response = await api.post(`/analytics/v2/refresh?full_refresh=${fullRefresh}`);
    return response.data;
  },
  initializeAnalytics: async () => {
    const response = await api.post('/analytics/v2/init');
    return response.data;
  },
  runTests: async () => {
    const response = await api.get('/analytics/v2/tests');
    return response.data;
  },
};

export default api;
